import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Message, Space, Tooltip } from '@arco-design/web-react';
import { IconDelete, IconEdit, IconPlus } from '@arco-design/web-react/icon';
import {
  Background,
  BackgroundVariant,
  BaseEdge,
  Controls,
  EdgeLabelRenderer,
  type EdgeProps,
  Handle,
  MarkerType,
  type Node,
  type NodeProps,
  NodeToolbar,
  Position,
  ReactFlow,
  type ReactFlowInstance,
  getSmoothStepPath,
  useNodesState,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import dagre from 'dagre';
import styled from 'styled-components';

import { IconFont } from '@/components';

// cspell:ignore dagre indegree rankdir nodesep ranksep marginx marginy reconnectable

const NODE_WIDTH = 180;
const NODE_HEIGHT = 64;

/** DAG 节点所需的最小任务结构，兼容任务模板和 TaskCluster 运行任务。 */
export interface TaskDagItem {
  name: string;
  /** 仅用于画布展示的节点名称，不参与依赖匹配。 */
  displayName?: string;
  role?: string;
  runStrategy?: string;
  dependsOn?: string[];
  /** 只读节点仅参与展示，不允许拖拽、连线或执行编辑操作。 */
  readonly?: boolean;
  /** 指向当前节点的生命周期事件边使用虚线样式，并禁止编辑。 */
  incomingEdgeVariant?: 'lifecycle';
}

export interface TaskDagNodeTone {
  border?: string;
  background?: string;
}

export interface TaskDagCanvasProps<T extends TaskDagItem> {
  tasks: T[];
  editable?: boolean;
  height?: number;
  renderMeta?: (task: T) => ReactNode;
  getNodeTone?: (task: T) => TaskDagNodeTone | undefined;
  /** 返回节点展示宽度；未传或小于默认宽度时使用默认值。 */
  getNodeWidth?: (task: T) => number | undefined;
  onAdd?: (parentName: string) => void;
  onEdit?: (name: string) => void;
  onDelete?: (name: string) => void;
  onNodeClick?: (name: string) => void;
  onConnect?: (source: string, target: string) => void;
  onReconnect?: (oldSource: string, oldTarget: string, source: string, target: string) => boolean | void;
  onDisconnect?: (source: string, target: string) => void;
  isValidConnection?: (source: string, target: string) => boolean;
}

function sortTopologically<T extends TaskDagItem>(tasks: T[]): T[] | null {
  const byName = new Map(tasks.map((task) => [task.name, task]));
  const indegree = new Map(tasks.map((task) => [task.name, 0]));
  const downstream = new Map(tasks.map((task) => [task.name, [] as string[]]));
  tasks.forEach((task) =>
    (task.dependsOn || []).forEach((dependency) => {
      if (!byName.has(dependency)) return;
      indegree.set(task.name, (indegree.get(task.name) || 0) + 1);
      downstream.get(dependency)?.push(task.name);
    }),
  );
  const queue = tasks.filter((task) => indegree.get(task.name) === 0).map((task) => task.name);
  const sorted: T[] = [];
  while (queue.length) {
    const name = queue.shift()!;
    sorted.push(byName.get(name)!);
    (downstream.get(name) || []).forEach((child) => {
      const degree = (indegree.get(child) || 0) - 1;
      indegree.set(child, degree);
      if (degree === 0) queue.push(child);
    });
  }
  return sorted.length === tasks.length ? sorted : null;
}

/** 新增依赖；拒绝自连、重复、未知节点和循环。 */
export function connectTaskDagDependency<T extends TaskDagItem>(tasks: T[], source: string, target: string): T[] | null {
  if (!source || !target || source === target) return null;
  const names = new Set(tasks.map((task) => task.name));
  if (!names.has(source) || !names.has(target)) return null;
  const targetTask = tasks.find((task) => task.name === target);
  if (targetTask?.dependsOn?.includes(source)) return null;
  return sortTopologically(
    tasks.map((task) => (task.name === target ? ({ ...task, dependsOn: [...(task.dependsOn || []), source] } as T) : task)),
  );
}

/** 删除任务依赖。 */
export function disconnectTaskDagDependency<T extends TaskDagItem>(tasks: T[], source: string, target: string): T[] {
  const next = tasks.map((task) =>
    task.name === target ? ({ ...task, dependsOn: (task.dependsOn || []).filter((name) => name !== source) } as T) : task,
  );
  return sortTopologically(next) || next;
}

/** 替换已有依赖；先移除旧边，再复用统一的 DAG 校验新增连接。 */
export function reconnectTaskDagDependency<T extends TaskDagItem>(
  tasks: T[],
  oldSource: string,
  oldTarget: string,
  source: string,
  target: string,
): T[] | null {
  return connectTaskDagDependency(disconnectTaskDagDependency(tasks, oldSource, oldTarget), source, target);
}

/** 重命名任务，并同步更新所有依赖引用。 */
export function renameTaskDagItem<T extends TaskDagItem>(tasks: T[], oldName: string, name: string): T[] | null {
  const nextName = name.trim();
  if (!nextName || (nextName !== oldName && tasks.some((task) => task.name === nextName))) return null;
  return tasks.map((task) => ({
    ...task,
    name: task.name === oldName ? nextName : task.name,
    dependsOn: task.dependsOn?.map((dependency) => (dependency === oldName ? nextName : dependency)),
  }));
}

/** 删除任务并清理其他任务对它的依赖。 */
export function removeTaskDagItem<T extends TaskDagItem>(tasks: T[], name: string): T[] {
  const next = tasks
    .filter((task) => task.name !== name)
    .map((task) =>
      task.dependsOn?.includes(name) ? ({ ...task, dependsOn: task.dependsOn.filter((dependency) => dependency !== name) } as T) : task,
    );
  return sortTopologically(next) || next;
}

/** 按依赖深度拆分执行层级；循环等异常数据放入末层兜底展示。 */
export function buildTaskDagLevels<T extends TaskDagItem>(tasks: T[]): T[][] {
  const names = new Set(tasks.map((task) => task.name));
  const remaining = new Set(names);
  const resolved = new Set<string>();
  const levels: T[][] = [];
  while (remaining.size) {
    const level = tasks.filter(
      (task) => remaining.has(task.name) && (task.dependsOn || []).filter((name) => names.has(name)).every((name) => resolved.has(name)),
    );
    const current = level.length ? level : tasks.filter((task) => remaining.has(task.name));
    levels.push(current);
    current.forEach((task) => {
      remaining.delete(task.name);
      resolved.add(task.name);
    });
  }
  return levels;
}

/** 获取没有下游依赖的末端任务名称，供只读收尾节点连接使用。 */
export function getTerminalTaskNames<T extends TaskDagItem>(tasks: T[]): string[] {
  const dependedOnNames = new Set(tasks.flatMap((task) => task.dependsOn || []));
  return tasks.filter((task) => !dependedOnNames.has(task.name)).map((task) => task.name);
}

const Canvas = styled.div<{ $height: number }>`
  height: ${({ $height }) => `${$height}px`};
  overflow: hidden;
  border: 1px solid var(--color-border-2);
  border-radius: 8px;
  background: var(--color-fill-1);

  .react-flow__controls {
    overflow: hidden;
    border: 1px solid var(--color-border-2);
    border-radius: 6px;
    box-shadow: 0 4px 12px rgba(29, 33, 41, 0.08);
  }

  .react-flow__controls-button {
    color: var(--color-text-2);
    background: var(--color-bg-2);
    border-bottom-color: var(--color-border-2);
  }

  .react-flow__edge.selected path {
    stroke: rgb(var(--primary-6));
  }
`;

const NodeCard = styled.div<{ $selected: boolean; $tone?: TaskDagNodeTone; $width: number }>`
  position: relative;
  width: ${({ $width }) => `${$width}px`};
  height: ${NODE_HEIGHT}px;
  overflow: visible;
  border: 1px solid ${({ $selected, $tone }) => ($selected ? 'rgb(var(--primary-6))' : $tone?.border || 'var(--color-border-2)')};
  border-radius: 8px;
  background: ${({ $tone }) => $tone?.background || 'var(--color-bg-2)'};
  box-shadow: ${({ $selected }) =>
    $selected ? '0 0 0 3px rgba(var(--primary-6), 0.12), 0 8px 24px rgba(29, 33, 41, 0.12)' : '0 4px 14px rgba(29, 33, 41, 0.08)'};
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease,
    transform 0.15s ease;

  &:hover {
    border-color: rgb(var(--primary-5));
    box-shadow: 0 8px 24px rgba(29, 33, 41, 0.12);
    transform: translateY(-1px);
  }
`;

const NodeAccent = styled.div`
  position: absolute;
  inset: 0 auto 0 0;
  width: 3px;
  background: linear-gradient(180deg, rgb(var(--primary-5)), rgb(var(--primary-7)));
`;

const NodeBody = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  height: 100%;
  padding: 10px 12px 10px 14px;
  box-sizing: border-box;
`;

const NodeIcon = styled.div`
  display: grid;
  flex: 0 0 34px;
  width: 34px;
  height: 34px;
  color: rgb(var(--primary-6));
  background: rgba(var(--primary-6), 0.1);
  border-radius: 8px;
  place-items: center;
`;

const NodeInfo = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 4px;
  align-items: flex-start;
  justify-content: center;
  height: 36px;
  min-width: 0;
`;

const NodeName = styled.span`
  display: block;
  width: 100%;
  overflow: hidden;
  color: var(--color-text-1);
  font-size: 14px;
  font-weight: 600;
  line-height: 18px;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const NodeMeta = styled.div`
  display: flex;
  gap: 6px;
  max-width: 100%;
  overflow: hidden;
  color: var(--color-text-3);
  font-size: 12px;
  line-height: 18px;
  white-space: nowrap;
`;

const NodeHandle = styled(Handle)<{ $hidden?: boolean }>`
  width: 12px;
  height: 12px;
  opacity: ${({ $hidden }) => ($hidden ? 0 : 1)};
  border: 2px solid var(--color-bg-2);
  background: rgb(var(--primary-6));
  cursor: ${({ $hidden }) => ($hidden ? 'default' : 'crosshair')};
  pointer-events: ${({ $hidden }) => ($hidden ? 'none' : 'auto')};
`;

interface SharedNodeData {
  name: string;
  displayName: string;
  width: number;
  meta: ReactNode;
  editable: boolean;
  hideHandles: boolean;
  tone?: TaskDagNodeTone;
  onAdd?: (name: string) => void;
  onEdit?: (name: string) => void;
  onDelete?: (name: string) => void;
}

function SharedTaskNode({ data, selected }: NodeProps) {
  const { t } = useTranslation();
  const task = data as unknown as SharedNodeData;
  return (
    <>
      {task.editable ? (
        <NodeToolbar isVisible={selected} position={Position.Top} className="nodrag nopan">
          <Space
            size={4}
            style={{ padding: 4, borderRadius: 6, background: 'var(--color-bg-2)', boxShadow: '0 4px 16px rgba(29, 33, 41, 0.14)' }}
          >
            {task.onAdd ? (
              <Tooltip content={t('data-center:maas.task.step.add')}>
                <Button size="mini" type="text" icon={<IconPlus />} onClick={() => task.onAdd?.(task.name)} />
              </Tooltip>
            ) : null}
            {task.onEdit ? (
              <Tooltip content={t('common:edit')}>
                <Button size="mini" type="text" icon={<IconEdit />} onClick={() => task.onEdit?.(task.name)} />
              </Tooltip>
            ) : null}
            {task.onDelete ? (
              <Tooltip content={t('common:delete')}>
                <Button size="mini" type="text" status="danger" icon={<IconDelete />} onClick={() => task.onDelete?.(task.name)} />
              </Tooltip>
            ) : null}
          </Space>
        </NodeToolbar>
      ) : null}
      <NodeCard $selected={selected} $tone={task.tone} $width={task.width} onDoubleClick={() => task.editable && task.onEdit?.(task.name)}>
        {task.hideHandles ? null : <NodeAccent />}
        <NodeHandle type="target" position={Position.Left} $hidden={task.hideHandles} isConnectable={!task.hideHandles} />
        <NodeBody>
          <NodeIcon>
            <IconFont name="icon-renwu" style={{ fontSize: 22 }} />
          </NodeIcon>
          <NodeInfo>
            <NodeName title={task.displayName}>{task.displayName}</NodeName>
            <NodeMeta>{task.meta}</NodeMeta>
          </NodeInfo>
        </NodeBody>
        <NodeHandle type="source" position={Position.Right} $hidden={task.hideHandles} isConnectable={!task.hideHandles} />
      </NodeCard>
    </>
  );
}

const NODE_TYPES = { task: SharedTaskNode };

interface SharedEdgeData {
  selected?: boolean;
  locked?: boolean;
  onDisconnect?: (source: string, target: string) => void;
}

/** 选中连线时在连线中点展示删除操作。 */
function SharedTaskEdge(props: EdgeProps) {
  const { t } = useTranslation();
  const [path, labelX, labelY] = getSmoothStepPath(props);
  const data = props.data as SharedEdgeData | undefined;
  return (
    <>
      <BaseEdge id={props.id} path={path} markerEnd={props.markerEnd} style={props.style} interactionWidth={props.interactionWidth} />
      {data?.selected && data.onDisconnect ? (
        <EdgeLabelRenderer>
          <div
            className="nodrag nopan"
            style={{ position: 'absolute', pointerEvents: 'all', transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)` }}
          >
            <Tooltip content={t('common:delete')}>
              <Button
                size="mini"
                shape="circle"
                status="danger"
                icon={<IconDelete />}
                aria-label={t('common:delete')}
                onClick={(event) => {
                  event.stopPropagation();
                  data.onDisconnect?.(props.source, props.target);
                }}
              />
            </Tooltip>
          </div>
        </EdgeLabelRenderer>
      ) : null}
    </>
  );
}

const EDGE_TYPES = { task: SharedTaskEdge };

function buildElements<T extends TaskDagItem>(tasks: T[], getNodeWidth?: (task: T) => number | undefined) {
  const graph = new dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));
  graph.setGraph({ rankdir: 'LR', nodesep: 48, ranksep: 72, marginx: 24, marginy: 24 });
  tasks.forEach((task) => graph.setNode(task.name, { width: Math.max(NODE_WIDTH, getNodeWidth?.(task) || 0), height: NODE_HEIGHT }));
  tasks.forEach((task) => (task.dependsOn || []).forEach((dependency) => graph.setEdge(dependency, task.name)));
  dagre.layout(graph);
  return {
    nodes: tasks.map((task) => {
      const position = graph.node(task.name);
      return {
        id: task.name,
        width: position.width,
        position: { x: position.x - position.width / 2, y: position.y - NODE_HEIGHT / 2 },
        data: {},
      };
    }),
    edges: tasks.flatMap((task) =>
      (task.dependsOn || []).map((dependency) => ({
        id: `${dependency}-${task.name}`,
        source: dependency,
        target: task.name,
        type: 'smoothstep',
        locked: task.readonly || task.incomingEdgeVariant === 'lifecycle',
        variant: task.incomingEdgeVariant,
      })),
    ),
  };
}

function calculateViewport(nodes: Node[], canvasWidth: number, canvasHeight: number) {
  if (!nodes.length || canvasWidth <= 0 || canvasHeight <= 0) return { x: 0, y: 0, zoom: 1 };
  const minX = Math.min(...nodes.map((node) => node.position.x));
  const minY = Math.min(...nodes.map((node) => node.position.y));
  const graphWidth = Math.max(...nodes.map((node) => node.position.x + (node.width || NODE_WIDTH))) - minX;
  const graphHeight = Math.max(...nodes.map((node) => node.position.y + NODE_HEIGHT)) - minY;
  const zoom = Math.max(0.4, Math.min(1, (canvasWidth - 96) / graphWidth, (canvasHeight - 64) / graphHeight));
  return { x: 48 - minX * zoom, y: (canvasHeight - graphHeight * zoom) / 2 - minY * zoom, zoom };
}

/** 创建、编辑和运行详情共用的任务 DAG 画布。 */
export function TaskDagCanvas<T extends TaskDagItem>({
  tasks,
  editable = false,
  height = tasks.length <= 3 ? 480 : 560,
  renderMeta,
  getNodeTone,
  getNodeWidth,
  onAdd,
  onEdit,
  onDelete,
  onNodeClick,
  onConnect,
  onReconnect,
  onDisconnect,
  isValidConnection,
}: TaskDagCanvasProps<T>) {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLDivElement>(null);
  const flowRef = useRef<ReactFlowInstance | null>(null);
  const reconnectingRef = useRef(false);
  const [selectedEdgeId, setSelectedEdgeId] = useState<string>();
  const elements = useMemo(() => buildElements(tasks, getNodeWidth), [getNodeWidth, tasks]);
  const flowNodes = useMemo(
    () =>
      elements.nodes.map((node) => {
        const task = tasks.find((item) => item.name === node.id)!;
        const nodeEditable = editable && !task.readonly;
        return {
          ...node,
          type: 'task',
          draggable: nodeEditable,
          connectable: nodeEditable,
          data: {
            name: task.name,
            displayName: task.displayName || task.name,
            width: node.width || NODE_WIDTH,
            meta: renderMeta?.(task) ?? (
              <>
                <span>{task.role || 'All'}</span>
                <span>·</span>
                <span>{task.runStrategy || '-'}</span>
              </>
            ),
            editable: nodeEditable,
            hideHandles: !!task.readonly,
            tone: getNodeTone?.(task),
            onAdd,
            onEdit,
            onDelete,
          },
        };
      }),
    [editable, elements.nodes, getNodeTone, onAdd, onDelete, onEdit, renderMeta, tasks],
  );
  const [nodes, setNodes, onNodesChange] = useNodesState(flowNodes);

  const alignLeft = useCallback(
    (instance = flowRef.current) => {
      const canvas = canvasRef.current;
      if (!instance || !canvas) return;
      instance.setViewport(calculateViewport(elements.nodes, canvas.clientWidth, height), { duration: 200 });
    },
    [elements.nodes, height],
  );

  useEffect(() => {
    setNodes(flowNodes);
    const frame = requestAnimationFrame(() => alignLeft());
    return () => cancelAnimationFrame(frame);
  }, [alignLeft, flowNodes, setNodes]);

  useEffect(() => {
    if (selectedEdgeId && !elements.edges.some((edge) => edge.id === selectedEdgeId)) setSelectedEdgeId(undefined);
  }, [elements.edges, selectedEdgeId]);

  return (
    <Canvas ref={canvasRef} $height={height}>
      <ReactFlow
        nodes={nodes}
        edges={elements.edges.map((edge) => ({
          ...edge,
          type: 'task',
          selectable: !edge.locked,
          deletable: !edge.locked,
          reconnectable: !edge.locked,
          data: {
            selected: !edge.locked && edge.id === selectedEdgeId,
            locked: edge.locked,
            onDisconnect:
              !edge.locked && editable && onDisconnect
                ? (source: string, target: string) => {
                    onDisconnect(source, target);
                    setSelectedEdgeId(undefined);
                  }
                : undefined,
          },
          interactionWidth: 24,
          markerEnd: {
            type: MarkerType.ArrowClosed,
            width: 14,
            height: 14,
            color:
              edge.variant === 'lifecycle'
                ? 'rgb(var(--orange-6))'
                : edge.id === selectedEdgeId
                  ? 'rgb(var(--primary-6))'
                  : 'var(--color-border-4)',
          },
          style: {
            stroke:
              edge.variant === 'lifecycle'
                ? 'rgb(var(--orange-6))'
                : edge.id === selectedEdgeId
                  ? 'rgb(var(--primary-6))'
                  : 'var(--color-border-4)',
            strokeWidth: 2,
            strokeDasharray: edge.variant === 'lifecycle' ? '6 5' : undefined,
          },
        }))}
        nodeTypes={NODE_TYPES}
        edgeTypes={EDGE_TYPES}
        nodesDraggable={editable}
        nodesConnectable={editable && !!onConnect}
        edgesReconnectable={editable && !!onReconnect}
        elementsSelectable={editable || !!onNodeClick}
        deleteKeyCode={['Backspace', 'Delete']}
        onNodesChange={onNodesChange}
        isValidConnection={(connection) =>
          !!connection.source &&
          !!connection.target &&
          (reconnectingRef.current || (isValidConnection?.(connection.source, connection.target) ?? true))
        }
        onConnect={(connection) => {
          if (connection.source && connection.target) onConnect?.(connection.source, connection.target);
        }}
        onConnectEnd={(_, state) => {
          if (state.toNode && state.isValid === false) Message.warning(t('data-center:maas.task.dag.connection.invalid'));
        }}
        onReconnectStart={() => {
          reconnectingRef.current = true;
        }}
        onReconnect={(edge, connection) => {
          if (connection.source && connection.target) {
            const accepted = onReconnect?.(edge.source, edge.target, connection.source, connection.target);
            if (accepted === false) Message.warning(t('data-center:maas.task.dag.connection.invalid'));
          }
        }}
        onReconnectEnd={() => {
          reconnectingRef.current = false;
        }}
        onEdgesDelete={(edges) => {
          edges.forEach((edge) => {
            if (!(edge.data as SharedEdgeData | undefined)?.locked) onDisconnect?.(edge.source, edge.target);
          });
          setSelectedEdgeId(undefined);
        }}
        onEdgeClick={(_, edge) => {
          if (!(edge.data as SharedEdgeData | undefined)?.locked) setSelectedEdgeId(edge.id);
        }}
        onPaneClick={() => setSelectedEdgeId(undefined)}
        onNodeClick={(_, node) => {
          setSelectedEdgeId(undefined);
          onNodeClick?.(node.id);
        }}
        minZoom={0.4}
        maxZoom={1.5}
        defaultViewport={{ x: 24, y: 184, zoom: 1 }}
        onInit={(instance) => {
          flowRef.current = instance;
          alignLeft(instance);
        }}
        proOptions={{ hideAttribution: true }}
      >
        <Background variant={BackgroundVariant.Dots} gap={20} size={1} color="var(--color-border-3)" />
        <Controls showInteractive={false} />
      </ReactFlow>
    </Canvas>
  );
}
